import { Int } from '@adornis/baseql/baseqlTypes.js';
import { registerSubscription } from '@adornis/baseql/metadata/register.js';
import { getCollectionHandle, getRawCollection } from '@adornis/baseql/server/collections.js';
import { publishFilter } from '@adornis/baseql/server/context.js';
import type { BaseQLSelectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { Observable, catchError, from, of, switchMap } from 'rxjs';
import type { PartnerAspect } from '../../master-data-management/partner/entities/aspects/PartnerAspect.js';
import { Partner } from '../../master-data-management/partner/entities/Partner.js';
import { Workshop } from '../entities/Workshop.js';
import { WorkshopAttendance } from '../entities/WorkshopAttendance.js';
import { WorkshopFilter } from '../entities/WorkshopFilter.js';
import { WorkshopTrainingAttendance } from '../entities/WorkshopTrainingAttendance.js';

export const subscribeWorkshopAllAttendanceSimple = registerSubscription({
  type: () => [WorkshopTrainingAttendance],
  operationName: 'subscribeWorkshopAllAttendanceSimple',
  resolve: (skip: number, limit: number, filter: WorkshopFilter) => {
    return (gqlFields: BaseQLSelectionSet<WorkshopTrainingAttendance>): Observable<WorkshopTrainingAttendance[]> => {
      const selector = {
        _class: {
          $in: [WorkshopAttendance._class, WorkshopTrainingAttendance._class],
        },
        ...filter.getMongoFilter(),
      };
      const handle = getCollectionHandle<WorkshopTrainingAttendance>(WorkshopAttendance._collectionName);
      return from(publishFilter(WorkshopAttendance._class)).pipe(
        switchMap(publishFilter => {
          return handle.watchQuery(publishFilter ? { $and: [publishFilter, selector] } : selector, {
            skip,
            limit,
          });
        }),
        switchMap(async attendances => {
          const partnerFilter = filter.getFilterAspect<PartnerAspect>('partnerID');

          const col = await getRawCollection<WorkshopAttendance>(WorkshopAttendance._collectionName);
          const res = await col
            .aggregate([
              { $match: { _id: { $in: attendances.map(x => x._id) } } },

              ...(filter.bundesland
                ? [
                    {
                      $lookup: {
                        from: Partner._collectionName,
                        localField: 'partnerID',
                        foreignField: '_id',
                        as: 'partners',
                        pipeline: [{ $project: { bundesland: 1 } }],
                      },
                    },

                    { $unwind: '$partners' },

                    { $match: { 'partners.bundesland': filter.bundesland } },
                  ]
                : []),

              ...(partnerFilter && (partnerFilter.value?.length ?? 0) > 0
                ? [
                    {
                      $lookup: {
                        from: 'adornisuser-materialized',
                        localField: 'userID',
                        foreignField: '_id',
                        as: 'user',
                        pipeline: [{ $project: { activeRelations: 1, inactiveRelations: 1 } }],
                      },
                    },

                    { $unwind: '$user' },

                    {
                      $match: {
                        $or: [
                          {
                            'user.activeRelations.object.schoolID': { $in: partnerFilter.value },
                          },
                          {
                            'user.inactiveRelations.object.schoolID': { $in: partnerFilter.value },
                          },
                        ],
                      },
                    },
                  ]
                : []),

              // Lookup workshop id
              {
                $lookup: {
                  from: Workshop._collectionName,
                  localField: 'workshopID',
                  foreignField: '_id',
                  as: 'workshop',
                  pipeline: [{ $project: { _id: 1, requiresTraining: 1 } }],
                },
              },

              // unwind
              { $unwind: '$workshop' },

              // if _class === WorkshopAttendance._class => $workshop.requireTraining === false or don't show
              // if _class === WorkshopTrainingAttendance._class => $workshop.requireTraining === true or don't show
              {
                $match: {
                  $or: [
                    {
                      $and: [{ _class: WorkshopAttendance._class }, { 'workshop.requiresTraining': false }],
                    },
                    {
                      $and: [{ _class: WorkshopAttendance._class }, { 'workshop.requiresTraining': null }],
                    },
                    {
                      $and: [{ _class: WorkshopTrainingAttendance._class }, { 'workshop.requiresTraining': true }],
                    },
                  ],
                },
              },
            ])
            .toArray();

          return res.map(r => ({
            ...r,
            _class: WorkshopTrainingAttendance._class,
            workshop: undefined,
          })) as WorkshopTrainingAttendance[];
        }),
        catchError(e => {
          return of([] as WorkshopTrainingAttendance[]);
        }),
      );
    };
  },
  params: [
    {
      name: 'skip',
      type: () => Int,
    },
    {
      name: 'limit',
      type: () => Int,
    },
    {
      name: 'filter',
      type: () => WorkshopFilter,
    },
  ],
});
